/**
 * Tree structure — owns the parent/children relationships for every
 * row in a Sheet. Single source of truth for sheet topology.
 *
 * Mutations emit Invalidation events through the shared sink using
 * lona-js's canonical vocabulary:
 *   - `insert`   → `NewRowInserted(rowId, sheetId)`
 *   - `reparent` / `delete` / `reorder` → `SheetStructureChanged`
 * Callers that need finer granularity inspect the store.
 */

import type { Option } from "../core/option.ts";
import {
  Actor,
  type InvalidationSink,
  NewRowInserted,
  SheetStructureChanged,
} from "../tx/invalidation.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { SheetId } from "../ids/sheet-id.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";

export class TreeStructure {
  static readonly ACTOR = new Actor("TreeStructure");

  readonly #parentOf = new Map<RowLocalId, Option<RowLocalId>>();
  readonly #childrenOf = new Map<Option<RowLocalId>, RowLocalId[]>();

  // Structural-change coalescing. `replaceSystemRows` drops + re-adds the whole
  // system-row set; without this each mutation emits its own
  // `SheetStructureChanged`, and each emit fans out synchronously to the sheet's
  // `recomputeComputedSources` handler AND the UI effect set (O(rows ×
  // dependents) — the multi-second rapid-switch freeze). `batchStructural`
  // buffers those emits and fires exactly ONE on drain.
  #deferStructural = false;
  #pendingStructureChanged = false;

  readonly #sheetId: SheetNetworkKey;
  /** `#sheetId` as a `SheetId` (`null`: a fixture sheet named by other text). */
  readonly #structureScope: Option<SheetId>;
  readonly #sink: InvalidationSink;
  constructor(
    sink: InvalidationSink,
    /** The owning sheet's id — the scope stamped on every structural emit so
     *  other sheets on the shared sink skip the rebuild. */
    sheetId: SheetNetworkKey,
  ) {
    this.#sink = sink;
    this.#sheetId = sheetId;
    this.#structureScope = SheetId.parse(sheetId);
  }

  /**
   * Run `fn` with structural-change emits coalesced: every `delete` /
   * `reparent` / `reorder` inside `fn` records a pending flag instead of
   * emitting, and a single `SheetStructureChanged` is emitted on drain.
   * Re-entrant — a nested call is transparent and only the outermost drains.
   * `NewRowInserted` (from `insert`) is intentionally NOT coalesced: it carries
   * a specific row id that subscribers may need.
   */
  batchStructural<T>(fn: () => T): T {
    if (this.#deferStructural) return fn();
    this.#deferStructural = true;
    this.#pendingStructureChanged = false;
    try {
      return fn();
    } finally {
      this.#deferStructural = false;
      if (this.#pendingStructureChanged) {
        this.#pendingStructureChanged = false;
        this.#sink.emit(new SheetStructureChanged(TreeStructure.ACTOR, this.#structureScope));
      }
    }
  }

  /** True while a `batchStructural` body is running — per-event listeners MAY
   *  skip work that the batch's single coalesced drain will redo globally. */
  get isBatching(): boolean {
    return this.#deferStructural;
  }

  #emitStructureChanged(): void {
    if (this.#deferStructural) {
      this.#pendingStructureChanged = true;
    } else {
      this.#sink.emit(
        new SheetStructureChanged(TreeStructure.ACTOR, this.#structureScope),
      );
    }
  }

  /** Return the parent of `id`, or `null` for a sheet-root row. */
  parent(id: RowLocalId): Option<RowLocalId> {
    return this.#parentOf.get(id) ?? null;
  }

  /** Return the ordered children of `id`. */
  children(id: RowLocalId): readonly RowLocalId[] {
    return this.#childrenOf.get(id) ?? EMPTY_CHILDREN;
  }

  /** Ordered ids of sheet-root rows. */
  roots(): readonly RowLocalId[] {
    return this.#childrenOf.get(null) ?? EMPTY_CHILDREN;
  }

  /** Insert `id` under `parent` at `position` (default: append). */
  insert(
    id: RowLocalId,
    parent: Option<RowLocalId>,
    position: Option<number>,
  ): void {
    const siblings = this.#getOrCreateChildren(parent);
    const pos = position ?? siblings.length;
    siblings.splice(pos, 0, id);
    this.#parentOf.set(id, parent);
    if (!this.#childrenOf.has(id)) this.#childrenOf.set(id, []);
    // Inside a batch the per-id event still fires (subscribers need the id),
    // but the insert ALSO arms the coalesced `SheetStructureChanged` drain —
    // per-event consumers that skip work under `isBatching` (the engine's own
    // recompute listeners) rely on the drain covering an insert-only batch.
    if (this.#deferStructural) this.#pendingStructureChanged = true;
    this.#sink.emit(new NewRowInserted(TreeStructure.ACTOR, id, this.#sheetId));
  }

  /** Move `id` to a new parent/position. */
  reparent(
    id: RowLocalId,
    newParent: Option<RowLocalId>,
    position: Option<number>,
  ): void {
    if (newParent != null) {
      if (newParent === id) {
        throw new Error(
          `tree.reparent: cannot reparent row ${id} under itself`,
        );
      }
      // Walk up from the proposed parent — if we encounter `id`,
      // the move would close a cycle. Any subsequent tree
      // traversal that follows parent links would infinite-loop.
      // Bounded by `seen`: a chain that loops without reaching `id` (a
      // corrupt tree; hydrate cuts wire cycles) closes no cycle through
      // `id`, so the walk stops there instead of spinning.
      const seen = new Set<RowLocalId>();
      let cursor: Option<RowLocalId> = newParent;
      while (cursor != null && !seen.has(cursor)) {
        seen.add(cursor);
        const next: Option<RowLocalId> = this.#parentOf.get(cursor) ?? null;
        if (next === id) {
          throw new Error(
            `tree.reparent: cannot reparent row ${id} under its own descendant ${newParent}`,
          );
        }
        cursor = next;
      }
    }
    const from = this.parent(id);
    const oldSiblings = this.#childrenOf.get(from);
    if (oldSiblings) {
      const idx = oldSiblings.indexOf(id);
      if (idx >= 0) oldSiblings.splice(idx, 1);
    }
    const newSiblings = this.#getOrCreateChildren(newParent);
    const pos = position ?? newSiblings.length;
    newSiblings.splice(pos, 0, id);
    this.#parentOf.set(id, newParent);
    this.#emitStructureChanged();
  }

  /** Remove `id` and all descendants. Returns the descendants for
   *  cleanup (e.g. `ModelRow.delete` clears cells for each). Each row is
   *  collected once (`seen`), so a corrupt tree whose children lists loop
   *  back never grows the walk without bound. */
  delete(id: RowLocalId): readonly RowLocalId[] {
    const parent = this.parent(id);
    const descendants: RowLocalId[] = [];
    const seen = new Set<RowLocalId>([id]);
    const stack = [...this.children(id)];
    for (let d = stack.pop(); d !== undefined; d = stack.pop()) {
      if (seen.has(d)) continue;
      seen.add(d);
      descendants.push(d);
      stack.push(...this.children(d));
    }
    const siblings = this.#childrenOf.get(parent);
    if (siblings) {
      const idx = siblings.indexOf(id);
      if (idx >= 0) siblings.splice(idx, 1);
    }
    for (const d of [id, ...descendants]) {
      this.#parentOf.delete(d);
      this.#childrenOf.delete(d);
    }
    this.#emitStructureChanged();
    return descendants;
  }

  /** Move `id` within the same parent. */
  reorder(id: RowLocalId, position: number): void {
    const parent = this.parent(id);
    const siblings = this.#childrenOf.get(parent);
    if (!siblings) return;
    const prev = siblings.indexOf(id);
    if (prev < 0 || prev === position) return;
    siblings.splice(prev, 1);
    siblings.splice(position, 0, id);
    this.#emitStructureChanged();
  }

  #getOrCreateChildren(parent: Option<RowLocalId>): RowLocalId[] {
    let list = this.#childrenOf.get(parent);
    if (!list) {
      list = [];
      this.#childrenOf.set(parent, list);
    }
    return list;
  }
}

const EMPTY_CHILDREN: readonly RowLocalId[] = Object.freeze([]);
